import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Users, 
  ChefHat, 
  Calendar, 
  TrendingUp, 
  Eye, 
  Ban, 
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Activity,
  Crown,
  Settings,
  Tag,
  Plus,
  Edit,
  Trash2,
  Gift,
  X,
  Bell
} from "lucide-react";
import axios from "axios";
import SubscriptionManagement from "./SubscriptionManagement";
import PlatformSettings from "./PlatformSettings";
import CampaignManager from "./CampaignManager";
import FinancialReports from "./FinancialReports";
// Firebase imports pour codes promo
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onClose }) => {
  const [stats, setStats] = useState({});
  const [workshops, setWorkshops] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [showEditPromo, setShowEditPromo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState('');
  
  // Tab management state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Page states for settings and reports
  const [showPlatformSettings, setShowPlatformSettings] = useState(false);
  const [showFinancialReports, setShowFinancialReports] = useState(false);
  const [showCampaignManager, setShowCampaignManager] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalData, setAlertModalData] = useState({
    title: '',
    message: '',
    type: 'success' // 'success' or 'error'
  });
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_percentage: '',
    discount_amount: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    applicable_plans: []
  });

  useEffect(() => {
    loadDashboardData();
    
    // Event listener pour ouvrir le gestionnaire de campagnes
    const handleOpenCampaignManager = () => {
      setShowCampaignManager(true);
    };
    
    window.addEventListener('openCampaignManager', handleOpenCampaignManager);
    
    return () => {
      window.removeEventListener('openCampaignManager', handleOpenCampaignManager);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      // Charger les données depuis Firebase ou utiliser des données de démo
      console.log('📊 Chargement dashboard admin depuis Firebase');
      
      // Charger les ateliers depuis Firestore
      try {
        const workshopsSnapshot = await getDocs(collection(db, 'workshops'));
        const workshopsData = workshopsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setWorkshops(workshopsData);
        console.log(`✅ ${workshopsData.length} ateliers chargés depuis Firestore`);
      } catch (error) {
        console.log('⚠️ Utilisation données démo pour les ateliers');
        setWorkshops([
          { id: 1, title: 'Macarons parisiens', chef: 'Chef Marie Dubois', status: 'active', participants: 8 },
          { id: 2, title: 'Cuisine méditerranéenne', chef: 'Chef Antoine Moreau', status: 'active', participants: 10 }
        ]);
      }

      // Charger les chefs depuis Firestore
      try {
        const chefsSnapshot = await getDocs(collection(db, 'chefs'));
        const chefsData = chefsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setChefs(chefsData);
        console.log(`✅ ${chefsData.length} chefs chargés depuis Firestore`);
      } catch (error) {
        console.log('⚠️ Utilisation données démo pour les chefs');
        setChefs([
          { id: 1, name: 'Chef Marie Dubois', email: 'marie.dubois@example.com', specialty: 'Pâtisserie française' },
          { id: 2, name: 'Chef Antoine Moreau', email: 'antoine.moreau@example.com', specialty: 'Cuisine méditerranéenne' }
        ]);
      }

      // Données de statistiques de démo
      setStats({
        total_workshops: 12,
        total_chefs: 21,
        total_bookings: 18,
        total_revenue: 2295
      });

      // Données de réservations de démo
      setBookings([
        { id: 1, workshop: 'Macarons parisiens', client: 'Marie Martin', date: '2024-02-15', status: 'confirmed' },
        { id: 2, workshop: 'Cuisine méditerranéenne', client: 'Pierre Durand', date: '2024-02-20', status: 'pending' }
      ]);
      
      // Charger les codes promo
      await loadPromoCodes();
      
      console.log('✅ Dashboard admin chargé avec succès');
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPromoCodes = async () => {
    try {
      // Charger les codes promo depuis Firestore
      const promoCodesSnapshot = await getDocs(collection(db, 'promo-codes'));
      const promoCodesData = promoCodesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPromoCodes(promoCodesData);
      console.log(`✅ ${promoCodesData.length} codes promo chargés depuis Firestore`);
    } catch (error) {
      console.error("Error loading promo codes from Firestore:", error);
      
      // Fallback vers des données de démo si Firestore échoue
      const demoPromoCodes = [
        {
          id: 'welcome2024',
          code: 'WELCOME2024',
          description: 'Code de bienvenue',
          discount_percentage: 20,
          max_uses: 100,
          used_count: 0,
          is_active: true
        },
        {
          id: 'kila50',
          code: 'KILA50',
          description: 'Réduction KILA France',
          discount_amount: 50,
          max_uses: 50,
          used_count: 0,
          is_active: true
        }
      ];
      setPromoCodes(demoPromoCodes);
      console.log('✅ Codes promo de démo chargés');
    }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    setPromoLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data
      const promoData = {
        code: newPromo.code.toUpperCase(),
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        valid_from: new Date(newPromo.valid_from).toISOString(),
        valid_until: new Date(newPromo.valid_until).toISOString(),
        applicable_plans: newPromo.applicable_plans
      };
      
      // Add discount type
      if (newPromo.discount_percentage) {
        promoData.discount_percentage = parseFloat(newPromo.discount_percentage) / 100;
      } else if (newPromo.discount_amount) {
        promoData.discount_amount = parseFloat(newPromo.discount_amount);
      }
      
      // Sauvegarder le code promo dans Firestore
      const promoId = `promo-${Date.now()}`;
      const promoRef = doc(db, 'promo-codes', promoId);
      const fullPromoData = {
        ...promoData,
        id: promoId,
        created_at: new Date(),
        used_count: 0,
        is_active: true
      };
      
      await setDoc(promoRef, fullPromoData);
      
      showAlert('Succès', 'Code promo créé avec succès !', 'success');
      setShowCreatePromo(false);
      setNewPromo({
        code: '',
        discount_percentage: '',
        discount_amount: '',
        max_uses: '',
        valid_from: '',
        valid_until: '',
        applicable_plans: []
      });
      await loadPromoCodes();
    } catch (error) {
      console.error("Error creating promo code:", error);
      showAlert('Erreur', error.response?.data?.detail || 'Erreur lors de la création du code promo', 'error');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleEditPromo = (promo) => {
    setEditingPromo(promo);
    setNewPromo({
      code: promo.code,
      discount_percentage: promo.discount_percentage ? (promo.discount_percentage * 100).toString() : '',
      discount_amount: promo.discount_amount ? promo.discount_amount.toString() : '',
      max_uses: promo.max_uses ? promo.max_uses.toString() : '',
      valid_from: new Date(promo.valid_from).toISOString().slice(0, 16),
      valid_until: new Date(promo.valid_until).toISOString().slice(0, 16),
      applicable_plans: promo.applicable_plans || []
    });
    setShowEditPromo(true);
  };

  const handleUpdatePromo = async (e) => {
    e.preventDefault();
    setPromoLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data
      const promoData = {
        code: newPromo.code.toUpperCase(),
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        valid_from: new Date(newPromo.valid_from).toISOString(),
        valid_until: new Date(newPromo.valid_until).toISOString(),
        applicable_plans: newPromo.applicable_plans
      };
      
      // Add discount type
      if (newPromo.discount_percentage) {
        promoData.discount_percentage = parseFloat(newPromo.discount_percentage) / 100;
      } else if (newPromo.discount_amount) {
        promoData.discount_amount = parseFloat(newPromo.discount_amount);
      }
      
      // Mettre à jour le code promo dans Firestore
      const promoRef = doc(db, 'promo-codes', editingPromo.id);
      const fullPromoData = {
        ...promoData,
        updated_at: new Date()
      };
      
      await updateDoc(promoRef, fullPromoData);
      
      showAlert('Succès', 'Code promo modifié avec succès !', 'success');
      setShowEditPromo(false);
      setEditingPromo(null);
      setNewPromo({
        code: '',
        discount_percentage: '',
        discount_amount: '',
        max_uses: '',
        valid_from: '',
        valid_until: '',
        applicable_plans: []
      });
      await loadPromoCodes();
    } catch (error) {
      console.error("Error updating promo code:", error);
      showAlert('Erreur', error.response?.data?.detail || 'Erreur lors de la modification du code promo', 'error');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleDeletePromo = async (promoId, promoCode) => {
    const confirmDelete = () => {
      setShowConfirmModal(false);
      executeDelete(promoId);
    };

    showConfirm(
      'Confirmer la suppression', 
      `Êtes-vous sûr de vouloir supprimer le code promo "${promoCode}" ?\n\nCette action est irréversible.`,
      confirmDelete
    );
  };

  const executeDelete = async (promoId) => {
    try {
      const token = localStorage.getItem('token');
      // Supprimer le code promo de Firestore
      const promoRef = doc(db, 'promo-codes', promoId);
      await deleteDoc(promoRef);
      
      showAlert('Succès', 'Code promo supprimé avec succès !', 'success');
      await loadPromoCodes();
    } catch (error) {
      console.error("Error deleting promo code:", error);
      showAlert('Erreur', error.response?.data?.detail || 'Erreur lors de la suppression du code promo', 'error');
    }
  };





  // Export handlers for financial reports
  const handleExportData = (format) => {
    const totalRevenue = getRevenueTotal();
    const totalBookings = bookings.length;
    const confirmedBookings = bookings.filter(b => b.status === 'confirmed').length;
    
    const exportData = {
      date: new Date().toLocaleDateString('fr-FR'),
      totalRevenue: `${totalRevenue}€`,
      totalBookings,
      confirmedBookings,
      conversionRate: totalBookings > 0 ? `${((confirmedBookings / totalBookings) * 100).toFixed(1)}%` : '0%',
      activeChefs: chefs.length,
      activeWorkshops: workshops.length,
      activePromoCodes: promoCodes.filter(p => p.is_active).length
    };

    if (format === 'excel') {
      // Simulate Excel export
      const csvContent = `CETROBON - Rapport Financier\nDate,${exportData.date}\n\nRécapitulatif\nChiffre d'affaires total,${exportData.totalRevenue}\nRéservations totales,${exportData.totalBookings}\nRéservations confirmées,${exportData.confirmedBookings}\nTaux de conversion,${exportData.conversionRate}\nFormateurs actifs,${exportData.activeChefs}\nAteliers disponibles,${exportData.activeWorkshops}\nCodes promo actifs,${exportData.activePromoCodes}`;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `rapport-financier-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showAlert('Export réussi', 'Le rapport financier a été exporté en CSV avec succès !', 'success');
    } else if (format === 'pdf') {
      showAlert('Export PDF', 'Fonctionnalité d\'export PDF en cours de développement.\n\nLe rapport sera bientôt disponible au format PDF avec graphiques et analyses détaillées.', 'success');
    }
  };

  const handleExportEmail = () => {
    showAlert('Envoi par email', 'Fonctionnalité d\'envoi par email en cours de développement.\n\nVous pourrez bientôt recevoir les rapports directement par email avec une fréquence configurable (quotidienne, hebdomadaire, mensuelle).', 'success');
  };

  const handleTogglePromoStatus = async (promoId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      // Activer/désactiver le code promo dans Firestore
      const promoRef = doc(db, 'promo-codes', promoId);
      await updateDoc(promoRef, { 
        is_active: !currentStatus,
        updated_at: new Date()
      });
      
      showAlert('Succès', response.data.message, 'success');
      await loadPromoCodes();
    } catch (error) {
      console.error("Error toggling promo status:", error);
      showAlert('Erreur', error.response?.data?.detail || 'Erreur lors de la modification du statut', 'error');
    }
  };

  const getRevenueTotal = () => {
    return bookings.reduce((total, booking) => total + (booking.total_price || 0), 0);
  };

  // Helper functions to replace window.confirm and alert
  const showConfirm = (title, message, onConfirm) => {
    setConfirmModalData({
      title,
      message,
      onConfirm,
      onCancel: () => setShowConfirmModal(false)
    });
    setShowConfirmModal(true);
  };

  const showAlert = (title, message, type = 'success') => {
    setAlertModalData({ title, message, type });
    setShowAlertModal(true);
    setTimeout(() => setShowAlertModal(false), 3000); // Auto close after 3 seconds
  };

  // Action handlers for Quick Actions buttons
  const handleManageSubscriptions = () => {
    setActiveTab('subscriptions');
  };

  const handlePlatformSettings = () => {
    setShowPlatformSettings(true);
  };

  const handleFinancialReports = () => {
    setShowFinancialReports(true);
  };

  // Workshop action handlers
  const handleViewWorkshop = (workshop) => {
    showAlert('Information', `Affichage des détails de l'atelier "${workshop.title}"...`, 'success');
  };

  const handleBanWorkshop = (workshop) => {
    const isSuspended = workshop.is_active === false || workshop.status === 'suspended';
    const action = isSuspended ? 'réactiver' : 'suspendre';
    const actionTitle = isSuspended ? 'Réactivation' : 'Suspension';
    
    const confirmAction = async () => {
      try {
        setShowConfirmModal(false);
        
        // Suspendre ou réactiver l'atelier dans Firestore
        const workshopRef = doc(db, 'workshops', workshop.id);
        const updateData = isSuspended ? {
          is_active: true,
          status: 'active',
          reactivated_at: new Date(),
          reactivated_by: 'Admin KILA France'
        } : {
          is_active: false,
          status: 'suspended',
          suspended_at: new Date(),
          suspended_by: 'Admin KILA France'
        };
        
        await updateDoc(workshopRef, updateData);
        
        // Mettre à jour la liste locale des ateliers
        const updatedWorkshops = workshops.map(w => 
          w.id === workshop.id ? { ...w, ...updateData } : w
        );
        setWorkshops(updatedWorkshops);
        
        const successMessage = isSuspended ? 
          `L'atelier "${workshop.title}" a été réactivé avec succès. Il est maintenant visible pour les clients.` :
          `L'atelier "${workshop.title}" a été suspendu avec succès. Il n'est plus visible pour les clients.`;
        
        showAlert('Succès', successMessage, 'success');
        console.log(`✅ Atelier ${action} dans Firestore:`, workshop.id);
      } catch (error) {
        console.error(`Erreur lors de la ${action} de l'atelier:`, error);
        showAlert('Erreur', `Erreur lors de la ${action} de l'atelier. Veuillez réessayer.`, 'error');
      }
    };

    const confirmMessage = isSuspended ?
      `Êtes-vous sûr de vouloir réactiver l'atelier "${workshop.title}" ?\n\nCet atelier redeviendra visible pour les clients.` :
      `Êtes-vous sûr de vouloir suspendre l'atelier "${workshop.title}" ?\n\nCet atelier ne sera plus visible pour les clients et marqué comme suspendu.`;

    showConfirm(
      `Confirmer la ${actionTitle.toLowerCase()}`,
      confirmMessage,
      confirmAction
    );
  };

  // Chef action handlers
  const handleViewChef = (chef) => {
    showAlert('Information', `Affichage du profil de ${chef.name}...`, 'success');
  };

  // Booking action handlers
  const handleViewBooking = (booking) => {
    const bookingDetails = `Détails de la réservation :
    
Client : ${booking.user_name}
Email : ${booking.user_email}
Participants : ${booking.participants_count}
Prix total : ${booking.total_price}€
Statut : ${booking.status === 'confirmed' ? 'Confirmée' : booking.status}`;
    
    showAlert('Détails de la réservation', bookingDetails, 'success');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Chargement du tableau de bord administrateur...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Show Platform Settings Page */}
      {showPlatformSettings ? (
        <PlatformSettings 
          user={user} 
          onClose={() => setShowPlatformSettings(false)} 
        />
      ) : showCampaignManager ? (
        <CampaignManager 
          user={user} 
          onClose={() => setShowCampaignManager(false)} 
        />
      ) : showFinancialReports ? (
        <FinancialReports 
          user={user} 
          onClose={() => setShowFinancialReports(false)} 
        />
      ) : (
        /* Show Main Admin Dashboard */
        <>
          {/* Header */}
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                <div>
                  <h1 className="text-2xl font-bold text-orange-600">CETROBON Admin</h1>
                  <p className="text-sm text-gray-600">Tableau de bord administrateur</p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    Connecté en tant que <strong>{user.name}</strong> (Admin)
                  </span>
                  <Button variant="outline" onClick={onClose}>
                    Retour au site
                  </Button>
                </div>
              </div>
            </div>
          </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Ateliers</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_workshops || 0}</div>
              <p className="text-xs text-muted-foreground">
                Ateliers disponibles sur la plateforme
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Formateurs</CardTitle>
              <ChefHat className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_chefs || 0}</div>
              <p className="text-xs text-muted-foreground">
                Chefs actifs sur la plateforme
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Réservations</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_bookings || 0}</div>
              <p className="text-xs text-muted-foreground">
                Réservations totales
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Chiffre d'affaires</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{getRevenueTotal()}€</div>
              <p className="text-xs text-muted-foreground">
                Revenus générés
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different sections */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="workshops">Ateliers</TabsTrigger>
            <TabsTrigger value="chefs">Formateurs</TabsTrigger>
            <TabsTrigger value="bookings">Réservations</TabsTrigger>
            <TabsTrigger value="subscriptions">Abonnements</TabsTrigger>
            <TabsTrigger value="promos">Codes Promo</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-orange-600">Résumé de la Plateforme</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Ateliers actifs</span>
                      <span className="font-semibold">{stats.total_workshops || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Chefs inscrits</span>
                      <span className="font-semibold">{stats.total_chefs || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Réservations totales</span>
                      <span className="font-semibold">{stats.total_bookings || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Revenus générés</span>
                      <span className="font-semibold text-green-600">{getRevenueTotal()}€</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-purple-600">Actions Rapides</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={handleManageSubscriptions}
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Gérer les Abonnements
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={handlePlatformSettings}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Paramètres Plateforme
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={handleFinancialReports}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Rapports Financiers
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => setShowCampaignManager(true)}
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Campagnes & Newsletter
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="workshops" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Gestion des Ateliers</CardTitle>
                <CardDescription>
                  Liste des ateliers disponibles sur la plateforme
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {workshops.map((workshop) => (
                    <div key={workshop.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold">{workshop.title}</h3>
                        <p className="text-sm text-gray-600">
                          Chef {workshop.chef_name} • {workshop.location} • {workshop.price}€
                        </p>
                        <div className="flex items-center mt-2">
                          <Badge variant="secondary">{workshop.category}</Badge>
                          <Badge 
                            variant={workshop.is_active === false || workshop.status === 'suspended' ? "destructive" : "default"}
                            className="ml-2"
                          >
                            {workshop.is_active === false || workshop.status === 'suspended' ? "Suspendu" : "Actif"}
                          </Badge>
                          <span className="text-xs text-gray-500 ml-2">
                            Max {workshop.max_participants} participants
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewWorkshop(workshop)}
                          title="Voir les détails de l'atelier"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleBanWorkshop(workshop)}
                          title={workshop.is_active === false || workshop.status === 'suspended' ? "Réactiver l'atelier" : "Suspendre l'atelier"}
                          className={workshop.is_active === false || workshop.status === 'suspended' ? "text-green-600 hover:text-green-700" : "text-red-600 hover:text-red-700"}
                        >
                          {workshop.is_active === false || workshop.status === 'suspended' ? 
                            <CheckCircle className="w-4 h-4" /> : 
                            <Ban className="w-4 h-4" />
                          }
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="chefs" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Gestion des Formateurs</CardTitle>
                <CardDescription>
                  Liste des chefs/formateurs inscrits
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {chefs.map((chef) => (
                    <div key={chef.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                          <ChefHat className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{chef.name}</h3>
                          <p className="text-sm text-gray-600">{chef.specialty}</p>
                          <p className="text-xs text-gray-500">
                            {chef.location} • {chef.workshops_count} ateliers
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="text-yellow-600">
                          ★ {chef.rating}
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewChef(chef)}
                          title="Voir le profil du formateur"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Gestion des Réservations</CardTitle>
                <CardDescription>
                  Liste des réservations clients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {bookings.length > 0 ? bookings.map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h3 className="font-semibold">{booking.user_name}</h3>
                        <p className="text-sm text-gray-600">{booking.user_email}</p>
                        <p className="text-xs text-gray-500">
                          {booking.participants_count} participants • {booking.total_price}€
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge 
                          variant={booking.status === 'confirmed' ? 'default' : 'secondary'}
                          className={booking.status === 'confirmed' ? 'bg-green-600' : ''}
                        >
                          {booking.status === 'confirmed' ? 'Confirmée' : booking.status}
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewBooking(booking)}
                          title="Voir les détails de la réservation"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )) : (
                    <p className="text-center text-gray-500 py-8">
                      Aucune réservation pour le moment
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="subscriptions" className="mt-6">
            <SubscriptionManagement user={user} />
          </TabsContent>

          <TabsContent value="promos" className="mt-6">
            <div className="space-y-6">
              {/* Header with create button */}
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">Gestion des Codes Promo</h3>
                  <p className="text-sm text-gray-600">
                    Créez et gérez les codes de réduction pour les chefs
                  </p>
                </div>
                <Button 
                  onClick={() => setShowCreatePromo(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Code
                </Button>
              </div>

              {/* Create promo form */}
              {showCreatePromo && (
                <Card className="border-orange-200">
                  <CardHeader>
                    <CardTitle className="text-orange-600">Créer un Code Promo</CardTitle>
                    <CardDescription>
                      Offrez des réductions spéciales aux chefs pour leurs abonnements
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreatePromo} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Code *</label>
                          <input
                            type="text"
                            value={newPromo.code}
                            onChange={(e) => setNewPromo({...newPromo, code: e.target.value})}
                            placeholder="Ex: BIENVENUE20"
                            className="w-full p-2 border rounded-md uppercase"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Utilisations max</label>
                          <input
                            type="number"
                            value={newPromo.max_uses}
                            onChange={(e) => setNewPromo({...newPromo, max_uses: e.target.value})}
                            placeholder="Ex: 100 (vide = illimité)"
                            className="w-full p-2 border rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Réduction (%)</label>
                          <input
                            type="number"
                            value={newPromo.discount_percentage}
                            onChange={(e) => setNewPromo({...newPromo, discount_percentage: e.target.value, discount_amount: ''})}
                            placeholder="Ex: 20 (pour 20%)"
                            className="w-full p-2 border rounded-md"
                            min="1"
                            max="100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Ou montant fixe (€)</label>
                          <input
                            type="number"
                            value={newPromo.discount_amount}
                            onChange={(e) => setNewPromo({...newPromo, discount_amount: e.target.value, discount_percentage: ''})}
                            placeholder="Ex: 50 (pour 50€)"
                            className="w-full p-2 border rounded-md"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Valide du *</label>
                          <input
                            type="datetime-local"
                            value={newPromo.valid_from}
                            onChange={(e) => setNewPromo({...newPromo, valid_from: e.target.value})}
                            className="w-full p-2 border rounded-md"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Valide jusqu'au *</label>
                          <input
                            type="datetime-local"
                            value={newPromo.valid_until}
                            onChange={(e) => setNewPromo({...newPromo, valid_until: e.target.value})}
                            className="w-full p-2 border rounded-md"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex space-x-3">
                        <Button 
                          type="submit" 
                          disabled={promoLoading}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          {promoLoading ? 'Création...' : 'Créer le Code'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setShowCreatePromo(false)}
                        >
                          Annuler
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Edit promo form */}
              {showEditPromo && editingPromo && (
                <Card className="border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-blue-600">Modifier le Code Promo</CardTitle>
                    <CardDescription>
                      Modifiez les paramètres du code "{editingPromo.code}"
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleUpdatePromo} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Code *</label>
                          <input
                            type="text"
                            value={newPromo.code}
                            onChange={(e) => setNewPromo({...newPromo, code: e.target.value})}
                            placeholder="Ex: BIENVENUE20"
                            className="w-full p-2 border rounded-md uppercase"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Utilisations max</label>
                          <input
                            type="number"
                            value={newPromo.max_uses}
                            onChange={(e) => setNewPromo({...newPromo, max_uses: e.target.value})}
                            placeholder="Ex: 100 (vide = illimité)"
                            className="w-full p-2 border rounded-md"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            Utilisations actuelles: {editingPromo.current_uses}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Réduction (%)</label>
                          <input
                            type="number"
                            value={newPromo.discount_percentage}
                            onChange={(e) => setNewPromo({...newPromo, discount_percentage: e.target.value, discount_amount: ''})}
                            placeholder="Ex: 20 (pour 20%)"
                            className="w-full p-2 border rounded-md"
                            min="1"
                            max="100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Ou montant fixe (€)</label>
                          <input
                            type="number"
                            value={newPromo.discount_amount}
                            onChange={(e) => setNewPromo({...newPromo, discount_amount: e.target.value, discount_percentage: ''})}
                            placeholder="Ex: 50 (pour 50€)"
                            className="w-full p-2 border rounded-md"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Valide du *</label>
                          <input
                            type="datetime-local"
                            value={newPromo.valid_from}
                            onChange={(e) => setNewPromo({...newPromo, valid_from: e.target.value})}
                            className="w-full p-2 border rounded-md"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Valide jusqu'au *</label>
                          <input
                            type="datetime-local"
                            value={newPromo.valid_until}
                            onChange={(e) => setNewPromo({...newPromo, valid_until: e.target.value})}
                            className="w-full p-2 border rounded-md"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex space-x-3">
                        <Button 
                          type="submit" 
                          disabled={promoLoading}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {promoLoading ? 'Modification...' : 'Enregistrer les modifications'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => {
                            setShowEditPromo(false);
                            setEditingPromo(null);
                          }}
                        >
                          Annuler
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Promo codes list */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Tag className="w-5 h-5 mr-2" />
                    Codes Promo Actifs ({promoCodes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {promoCodes.length > 0 ? promoCodes.map((promo) => (
                      <div key={promo.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <Badge className="bg-orange-600 text-white font-mono">
                                {promo.code}
                              </Badge>
                              <div className="text-sm text-gray-600">
                                {promo.discount_percentage 
                                  ? `${(promo.discount_percentage * 100).toFixed(0)}% de réduction`
                                  : `${promo.discount_amount}€ de réduction`
                                }
                              </div>
                              <Badge variant={promo.is_active ? "default" : "secondary"}>
                                {promo.is_active ? "Actif" : "Inactif"}
                              </Badge>
                            </div>
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-500">
                              <div>
                                Utilisations: {promo.current_uses}/{promo.max_uses || '∞'}
                              </div>
                              <div>
                                Valide du: {new Date(promo.valid_from).toLocaleDateString('fr-FR')}
                              </div>
                              <div>
                                Jusqu'au: {new Date(promo.valid_until).toLocaleDateString('fr-FR')}
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditPromo(promo)}
                              title="Modifier le code promo"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className={promo.is_active ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"}
                              onClick={() => handleTogglePromoStatus(promo.id, promo.is_active)}
                              title={promo.is_active ? "Désactiver le code" : "Activer le code"}
                            >
                              {promo.is_active ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeletePromo(promo.id, promo.code)}
                              title="Supprimer le code promo"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-8">
                        <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">Aucun code promo créé pour le moment</p>
                        <Button 
                          onClick={() => setShowCreatePromo(true)}
                          className="mt-4 bg-orange-600 hover:bg-orange-700"
                        >
                          Créer le premier code
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-orange-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {confirmModalData.title}
                </h3>
              </div>
              <p className="text-gray-600 mb-6 whitespace-pre-line">
                {confirmModalData.message}
              </p>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={confirmModalData.onCancel}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={confirmModalData.onConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Confirmer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {alertModalData.type === 'success' ? (
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {alertModalData.title}
                  </h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAlertModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-gray-600 mb-4">
                {alertModalData.message}
              </p>
              <div className="flex justify-end">
                <Button 
                  onClick={() => setShowAlertModal(false)}
                  className={alertModalData.type === 'success' 
                    ? "bg-green-600 hover:bg-green-700" 
                    : "bg-red-600 hover:bg-red-700"
                  }
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-orange-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {confirmModalData.title}
                </h3>
              </div>
              <p className="text-gray-600 mb-6 whitespace-pre-line">
                {confirmModalData.message}
              </p>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={confirmModalData.onCancel}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={confirmModalData.onConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Confirmer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  {alertModalData.type === 'success' ? (
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {alertModalData.title}
                  </h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAlertModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-gray-600 mb-4 whitespace-pre-line">
                {alertModalData.message}
              </p>
              <div className="flex justify-end">
                <Button 
                  onClick={() => setShowAlertModal(false)}
                  className={alertModalData.type === 'success' 
                    ? "bg-green-600 hover:bg-green-700" 
                    : "bg-red-600 hover:bg-red-700"
                  }
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;